import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { ASSET_TYPES, findAssetType } from './assetTypes';
import SharedAssetFields from './SharedAssetFields';
import ContinuityAssetDocuments from './ContinuityAssetDocuments';
import AutomobileForm from './forms/AutomobileForm';
import LifeInsuranceForm from './forms/LifeInsuranceForm';
import PrivateJetForm from './forms/PrivateJetForm';
import YachtForm from './forms/YachtForm';

const FORM_BY_KEY = {
  automobile: AutomobileForm,
  life_insurance: LifeInsuranceForm,
  private_jet: PrivateJetForm,
  yacht: YachtForm,
};

const emptyShared = { nickname: '', estimated_value: null, currency: '', include_in_net_worth: false, notes: '' };

const AddContinuityAssetModal = ({ open, onClose, onSubmit, editing }) => {
  const [step, setStep] = useState('picker');
  const [pickedType, setPickedType] = useState(null);
  const [shared, setShared] = useState(emptyShared);
  const [details, setDetails] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setPickedType(editing.asset_type);
      setShared({
        nickname: editing.nickname || '',
        estimated_value: editing.estimated_value ?? null,
        currency: editing.currency || '',
        include_in_net_worth: !!editing.include_in_net_worth,
        notes: editing.notes || '',
      });
      setDetails(editing.details || {});
      setStep('form');
    } else {
      setPickedType(null);
      setShared(emptyShared);
      setDetails({});
      setStep('picker');
    }
  }, [open, editing]);

  const pick = (typeKey) => { setPickedType(typeKey); setStep('form'); };

  // ``keepOpen`` toggle enables the "Save & add another" flow — a lightweight
  // batch mode that keeps the modal open on the same asset type after a
  // successful save, wiping the shared/detail fields so the user can rapidly
  // enter several cars, policies, etc. in one sitting.
  const submitInternal = async (keepOpen) => {
    if (!shared.nickname.trim()) { toast.error('Please give this asset a name.'); return; }
    setSaving(true);
    try {
      await onSubmit({ asset_type: pickedType, ...shared, details });
      toast.success(editing ? 'Asset updated.' : 'Asset added.');
      if (keepOpen && !editing) {
        // Reset fields but stay on the same asset type — perfect for
        // "I have three cars to add" without closing between each one.
        setShared(emptyShared);
        setDetails({});
      } else {
        onClose();
      }
    } catch (err) {
      toast.error(err.message || 'Could not save.');
    } finally { setSaving(false); }
  };

  const submit = async (e) => { e.preventDefault(); await submitInternal(false); };

  const FormComp = pickedType ? FORM_BY_KEY[pickedType] : null;
  const meta = pickedType ? findAssetType(pickedType) : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-continuity-asset-modal">
        <DialogHeader>
          <DialogTitle>
            {step === 'picker' ? 'Add a Continuity Asset' : `${editing ? 'Edit' : 'Add'} ${meta?.singular || 'asset'}`}
          </DialogTitle>
        </DialogHeader>

        {step === 'picker' && (
          <div className="grid grid-cols-2 gap-3 mt-2">
            {ASSET_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key} type="button" onClick={() => pick(t.key)}
                  data-testid={`ca-picker-${t.key}`}
                  className="flex flex-col items-start p-4 rounded-xl border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 transition text-left"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5 text-slate-700" />
                    <span className={`text-xs px-2 py-0.5 rounded-full ${t.accent.badge}`}>{t.label}</span>
                  </div>
                  <p className="text-sm text-slate-600">Add {t.singular} to your Continuity Assets.</p>
                </button>
              );
            })}
          </div>
        )}

        {step === 'form' && FormComp && (
          <form onSubmit={submit} className="space-y-4 mt-2">
            {!editing && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setStep('picker')} className="text-slate-600">
                <ArrowLeft className="w-4 h-4 mr-1" />Change asset type
              </Button>
            )}
            <SharedAssetFields value={shared} onChange={setShared} />
            <div className="border-t border-slate-200 pt-4">
              <FormComp details={details} onChange={setDetails} />
            </div>
            {editing && (
              <div className="border-t border-slate-200 pt-4">
                <ContinuityAssetDocuments assetId={editing.asset_id} />
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              {!editing && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => submitInternal(true)}
                  data-testid="ca-save-and-add-another-btn"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                >
                  {saving ? 'Saving…' : 'Save & add another'}
                </Button>
              )}
              <Button type="submit" disabled={saving} data-testid="ca-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add asset'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddContinuityAssetModal;
